/**
 * Pure derivation of per-field UI state for one scope from the GLOBAL value set.
 * Derived effects only (visibility, enablement, required, options,
 * dateConstraints); reactive effects (reset/autoFill) are handled by
 * `applyFieldReactions`.
 *
 * Conflict resolution:
 * - visibility/enablement/applicability: restrictive wins. A field with a
 *   `show:true` (`enable:true` / `applicable:true`) rule is hidden (disabled /
 *   not-applicable) by default and only shown (enabled / applicable) when one
 *   such rule passes; any passing `show:false` (`enable:false` /
 *   `applicable:false`) rule forces it hidden (disabled / not-applicable).
 * - required: any passing `required:true` -> required.
 * - options / dateConstraints: last passing rule wins (registry order matters).
 */
import type {
    DateConstraint,
    DerivableScope,
    FieldRule,
    OptionsSource,
    QualifiedId,
    RuleOption,
} from '../../config/fieldRulesConfig';
import type { FieldUiState, OptionResolvers, ResolvedDateConstraints, RuleContext, RuleValues } from '../types';
import { evaluateCondition, resolveSource } from './evaluateCondition';

const targetsOf = (rule: FieldRule): QualifiedId[] => (Array.isArray(rule.target) ? rule.target : [rule.target]);

const defaultState = (): FieldUiState => ({ visible: true, disabled: false, required: false, applicable: true });

function resolveOptions(
    source: OptionsSource,
    target: QualifiedId,
    values: RuleValues,
    resolvers: OptionResolvers,
): RuleOption[] | undefined {
    switch (source.kind) {
        case 'static':
            return source.options;
        case 'mapByField':
            return source.map[String(values[source.field])] ?? [];
        case 'dynamic':
            return resolvers.dynamic?.(source.key, source.byField ? values[source.byField] : undefined);
        case 'filterByParent':
            return resolvers.filterByParent?.({
                parentField: source.parentField,
                dimension: source.dimension,
                attributeType: source.attributeType,
                parentValue: values[source.parentField],
            });
        case 'disableByMap': {
            const base = resolvers.baseOptions?.(target) ?? [];
            const current = values[source.field];
            const allowed =
                current !== undefined && current !== null && current !== ''
                    ? source.map[String(current)] ?? []
                    : source.defaultEnabled ?? [];
            return base.map((opt) => ({
                ...opt,
                disabled: !(allowed.includes(opt.value) || opt.value === current),
            }));
        }
        default:
            return undefined;
    }
}

function resolveDate(constraints: DateConstraint, values: RuleValues, ctx: RuleContext): ResolvedDateConstraints {
    return {
        minDate: constraints.minField ? resolveSource(constraints.minField, values, ctx) : undefined,
        maxDate: constraints.maxField ? resolveSource(constraints.maxField, values, ctx) : undefined,
        disablePast: constraints.disablePast,
        minFromContext: constraints.minFromContext,
        allowedYears: constraints.allowedYears,
    };
}

export function deriveFieldStatesForScope(
    rules: FieldRule[],
    scope: DerivableScope,
    values: RuleValues,
    ctx: RuleContext,
    resolvers: OptionResolvers = {},
): Record<string, FieldUiState> {
    const prefix = `${scope}:`;

    const result: Record<string, FieldUiState> = {};
    const hasShowAllow: Record<string, boolean> = {};
    const showPass: Record<string, boolean> = {};
    const hidePass: Record<string, boolean> = {};
    const hasEnableAllow: Record<string, boolean> = {};
    const enablePass: Record<string, boolean> = {};
    const disablePass: Record<string, boolean> = {};
    const requiredPass: Record<string, boolean> = {};
    const hasApplicableAllow: Record<string, boolean> = {};
    const applicablePass: Record<string, boolean> = {};
    const notApplicablePass: Record<string, boolean> = {};

    const ensure = (field: string): FieldUiState => {
        if (!result[field]) result[field] = defaultState();
        return result[field];
    };

    for (const rule of rules) {
        for (const target of targetsOf(rule)) {
            if (!target.startsWith(prefix)) continue;
            const field = target.slice(prefix.length);
            ensure(field);
            const passes = evaluateCondition(rule.when, values, ctx);
            const effect = rule.effect;

            switch (effect.kind) {
                case 'visibility':
                    if (effect.show) {
                        hasShowAllow[field] = true;
                        if (passes) showPass[field] = true;
                    } else if (passes) {
                        hidePass[field] = true;
                    }
                    break;
                case 'enablement':
                    if (effect.enable) {
                        hasEnableAllow[field] = true;
                        if (passes) enablePass[field] = true;
                    } else if (passes) {
                        disablePass[field] = true;
                    }
                    break;
                case 'required':
                    if (passes && effect.required) requiredPass[field] = true;
                    break;
                case 'options':
                    // Last passing rule wins.
                    if (passes) ensure(field).options = resolveOptions(effect.source, target, values, resolvers);
                    break;
                case 'dateConstraints':
                    if (passes) ensure(field).dateConstraints = resolveDate(effect.constraints, values, ctx);
                    break;
                case 'applicability':
                    if (effect.applicable) {
                        hasApplicableAllow[field] = true;
                        if (passes) applicablePass[field] = true;
                    } else if (passes) {
                        notApplicablePass[field] = true;
                    }
                    break;
                default:
                    break;
            }
        }
    }

    for (const field of Object.keys(result)) {
        const state = result[field];
        if (hasShowAllow[field]) state.visible = !!showPass[field];
        if (hidePass[field]) state.visible = false;
        if (hasEnableAllow[field]) state.disabled = !enablePass[field];
        if (disablePass[field]) state.disabled = true;
        state.required = !!requiredPass[field];
        if (hasApplicableAllow[field]) state.applicable = !!applicablePass[field];
        if (notApplicablePass[field]) state.applicable = false;
    }

    return result;
}
